// Centralized pure calculation functions for Estimate v2

// Compute core product calculations including weight, making, tax, totals and offer effects
export function computeProductCalculations(productFormData = {}, offers = []) {
  const {
    gross_weight = 0,
    stone_weight = 0,
    stone_price = 0,
    va_percent = 0,
    va_on = "Gross Weight",
    rate = 0,
    mc_per_gram = 0,
    hm_charges = 0,
    tax_percent = 0,
    disscount = 0,
    festival_discount = 0,
    qty = 0,
    pieace_cost = 0,
    pricing = "By Weight",
    pricingOpt="Net Weight",
    mc_on = "MC %",
    making_charges = 0,
    total_price = 0,
    original_total_price = 0,
    original_piece_taxable_amt = 0,
  } = productFormData || {};

  const grossWeight = parseFloat(gross_weight) || 0;
  const stoneWeight = parseFloat(stone_weight) || 0;
  const stonePrice = parseFloat(stone_price) || 0;
  const vaPercent = parseFloat(va_percent) || 0;
  const rateValue = parseFloat(rate) || 0;
  const mcPerGram = parseFloat(mc_per_gram) || 0;
  const hmCharges = parseFloat(hm_charges) || 0;
  const taxPercent = parseFloat(tax_percent) || 0;
  const discount = parseFloat(disscount) || 0;
  const festivalDiscount = parseFloat(festival_discount) || 0;
  const qtyValue = parseFloat(qty) || 0;
  const pieceCost = parseFloat(pieace_cost) || 0;
  const mrpPriceValue = parseFloat(productFormData.mrp_price) || 0;
  const makingChargesValue = parseFloat(making_charges) || 0; // kept for readability if needed later
  // const totalPriceValue = parseFloat(total_price) || 0; // unused

  const baseWeight =
    pricingOpt === "Net Weight"
      ? grossWeight - stoneWeight
      : grossWeight;
  // Basic weights
  const weightBW = grossWeight - stoneWeight;
  const wastageWeight = va_on === "Gross Weight" ? (grossWeight * vaPercent) / 100 : (weightBW * vaPercent) / 100;
  const totalWeightAW = baseWeight + wastageWeight;

  // Rate amount
  let rateAmt = 0;
  if (pricing === "By fixed" || pricing === "By Running Tag") {
    rateAmt = pieceCost * qtyValue;
  } else {
    rateAmt = rateValue * totalWeightAW;
  }

  // Making charges
  let calculatedMakingCharges = 0;
  let calculatedMcPerGram = null;
  switch (mc_on) {
    case "MC %":
      calculatedMakingCharges = (mcPerGram * rateAmt) / 100;
      break;
    case "MC / Gram":
      calculatedMakingCharges = mcPerGram * totalWeightAW;
      break;
    case "MC / Piece":
      if (makingChargesValue && totalWeightAW > 0) {
        calculatedMcPerGram = makingChargesValue / totalWeightAW;
        calculatedMakingCharges = makingChargesValue;
      }
      break;
    default:
      calculatedMakingCharges = makingChargesValue;
  }

  // Tax and totals
  let taxAmt = 0;
  let calculatedTotalPrice = 0;
  let pieceTaxableAmt = 0;
  let mrpPrice = 0;

  if (pricing === "By fixed" || pricing === "By Running Tag") {
    // For fixed products and running tag products, if mrp_price is available, use it as the base price
    // mrp_price is typically the selling price (price per piece)
    // If mrp_price exists and is greater than 0, use it as the taxable amount per piece
    // Otherwise, use piece_cost as before
    if (mrpPriceValue > 0 && pricing!= "By Running Tag") {
      // Use mrp_price as the taxable amount per piece (selling price before tax)
      // This matches the behavior in Orders and Sales where mrp_price is the product price
      pieceTaxableAmt = mrpPriceValue * qtyValue;
      taxAmt = (taxPercent * pieceTaxableAmt) / 100;
      calculatedTotalPrice = pieceTaxableAmt + taxAmt;
      mrpPrice = mrpPriceValue;
    } else if (pieceCost > 0) {
      // Fallback to piece_cost if mrp_price is not available
      pieceTaxableAmt = pieceCost * qtyValue;
      taxAmt = (taxPercent * pieceTaxableAmt) / 100;
      calculatedTotalPrice = pieceTaxableAmt + taxAmt;
      mrpPrice = qtyValue > 0 ? calculatedTotalPrice / qtyValue : 0;
    } else {
      // If neither mrp_price nor piece_cost is available, set defaults
      pieceTaxableAmt = 0;
      taxAmt = 0;
      calculatedTotalPrice = 0;
      mrpPrice = 0;
    }
  } else {
    const totalDiscount = discount + festivalDiscount;
    const taxable = rateAmt + stonePrice + calculatedMakingCharges + hmCharges - totalDiscount;
    pieceTaxableAmt = taxable; // Set piece_taxable_amt for weight-based pricing
    taxAmt = (taxable * taxPercent) / 100;
    calculatedTotalPrice = taxable + taxAmt;
  }

  // Offer calculations
  let offerCalculations = {};
  if (offers && offers.length > 0) {
    const selectedOffer = offers[0];
    const percentageDiscount = parseFloat(selectedOffer.discount_percentage) || 0;
    const rateDiscount = parseFloat(selectedOffer.discount_on_rate) || 0;
    const fixedPercentageDiscount = parseFloat(selectedOffer.discount_percent_fixed) || 0;

    if (pricing === "By fixed" || pricing === "By Running Tag") {
      const originalPieceTaxableAmtValue = original_piece_taxable_amt ? parseFloat(original_piece_taxable_amt) : pieceTaxableAmt;
      const calculatedDiscount = (pieceTaxableAmt * fixedPercentageDiscount) / 100;
      const updatedPieceTaxableAmt = pieceTaxableAmt - calculatedDiscount;
      const offerTaxAmt = (taxPercent * updatedPieceTaxableAmt) / 100;
      const offerTotalPrice = updatedPieceTaxableAmt + offerTaxAmt;

      offerCalculations = {
        original_piece_taxable_amt: originalPieceTaxableAmtValue.toFixed(2),
        festival_discount: calculatedDiscount.toFixed(2),
        festival_discount_percentage: percentageDiscount.toFixed(2),
        festival_discount_on_rate: rateDiscount.toFixed(2),
        piece_taxable_amt: updatedPieceTaxableAmt.toFixed(2),
        tax_amt: offerTaxAmt.toFixed(2),
        total_price: offerTotalPrice.toFixed(2),
      };
    } else {
      const weightBasedDiscount = (rateDiscount / 10) * grossWeight;
      const makingChargesDiscount = (calculatedMakingCharges * percentageDiscount) / 100;
      const calculatedDiscount = makingChargesDiscount + weightBasedDiscount;
      const totalBeforeTax = rateAmt + stonePrice + calculatedMakingCharges + hmCharges - calculatedDiscount - discount;
      const offerTaxAmt = (totalBeforeTax * taxPercent) / 100;
      const offerTotalPrice = totalBeforeTax + offerTaxAmt;

      offerCalculations = {
        original_total_price: original_total_price || parseFloat(total_price || 0).toFixed(2),
        original_piece_taxable_amt: pieceTaxableAmt.toFixed(2), // Store original piece_taxable_amt
        festival_discount: calculatedDiscount.toFixed(2),
        festival_discount_percentage: percentageDiscount.toFixed(2),
        festival_discount_on_rate: rateDiscount.toFixed(2),
        piece_taxable_amt: totalBeforeTax.toFixed(2), // Update piece_taxable_amt for weight-based pricing
        tax_amt: offerTaxAmt.toFixed(2),
        total_price: offerTotalPrice.toFixed(2),
      };
    }
  }

  // Reverse calculation for MC per gram when total price is manually changed
  const mcPerGramFromTotal = computeMcPerGramFromTotalPrice({
    productFormData,
  });

  return {
    weight_bw: weightBW.toFixed(2),
    wastage_weight: wastageWeight.toFixed(2),
    total_weight_av: totalWeightAW.toFixed(2),
    rate_amt: rateAmt.toFixed(2),
    making_charges: calculatedMakingCharges.toFixed(2),
    tax_amt: taxAmt.toFixed(2),
    total_price: calculatedTotalPrice.toFixed(2),
    piece_taxable_amt: pieceTaxableAmt.toFixed(2),
    mrp_price: mrpPrice.toFixed(2),
    ...(calculatedMcPerGram !== null && { mc_per_gram: calculatedMcPerGram.toFixed(2) }),
    ...offerCalculations,
    calculateMcPerGramFromTotalPrice: mcPerGramFromTotal,
  };
}

// Compute MC per gram from a target total price given product state
export function computeMcPerGramFromTotalPrice({ productFormData = {} }) {
  const {
    gross_weight = 0,
    stone_weight = 0,
    stone_price = 0,
    va_percent = 0,
    va_on = "Gross Weight",
    rate = 0,
    mc_on = "MC %",
    hm_charges = 0,
    tax_percent = 0,
    disscount = 0,
    total_price = 0,
    rate_amt,
  } = productFormData || {};

  const grossWeight = parseFloat(gross_weight) || 0;
  const stoneWeight = parseFloat(stone_weight) || 0;
  const vaPercent = parseFloat(va_percent) || 0;
  const rateValue = parseFloat(rate) || 0;
  const taxPercent = parseFloat(tax_percent) || 0;
  const discount = parseFloat(disscount) || 0;
  const totalPriceValue = parseFloat(total_price) || 0;

  const weightBW = grossWeight - stoneWeight;
  const wastageWeight = va_on === "Gross Weight" ? (grossWeight * vaPercent) / 100 : (weightBW * vaPercent) / 100;
  const totalWeightAW = weightBW + wastageWeight;

  const computedRateAmt = typeof rate_amt !== 'undefined' ? parseFloat(rate_amt) || 0 : rateValue * totalWeightAW;

  if (!isNaN(totalPriceValue) && totalPriceValue > 0 && taxPercent > 0 && computedRateAmt > 0) {
    const taxableAmount = (totalPriceValue * 100) / (100 + taxPercent);
    let mcPerGramResult = 0;

    if (mc_on === "MC %") {
      const numerator = taxableAmount - computedRateAmt - hm_charges + discount + stone_price;
      const denominator = computedRateAmt;
      mcPerGramResult = denominator !== 0 ? (numerator * 100) / denominator : 0;
    } else if (mc_on === "MC / Gram" || mc_on === "MC / Piece") {
      const totalMC = taxableAmount - computedRateAmt - (parseFloat(hm_charges) || 0) - (parseFloat(stone_price) || 0) + discount;
      if (totalWeightAW > 0) {
        mcPerGramResult = totalMC / totalWeightAW;
      }
    }

    return !isNaN(mcPerGramResult) ? mcPerGramResult : null;
  }
  return null;
}

// Apply festival discount to a single product
export function applyFestivalDiscountToProductCalc(product = {}, offer = {}) {
  const taxPercent = parseFloat(product.tax_percent) || 1;
  const pieceCost = parseFloat(product.pieace_cost) || 0;
  const qty = parseFloat(product.qty) || 1;
  const discountAmt = parseFloat(product.disscount) || 0;
  const pricingType = product.pricing;
  const rateAmt = parseFloat(product.rate_amt) || 0;
  const stonePrice = parseFloat(product.stone_price) || 0;
  const makingCharges = parseFloat(product.making_charges) || 0;
  const hmCharges = parseFloat(product.hm_charges) || 0;
  const grossWeight = parseFloat(product.gross_weight) || 0;

  const percentageDiscount = parseFloat(offer.discount_percentage) || 0;
  const rateDiscount = parseFloat(offer.discount_on_rate) || 0;
  const fixedPercentageDiscount = parseFloat(offer.discount_percent_fixed) || 0;

  if (pricingType === "By fixed" || pricingType === "By Running Tag") {
    const pieceTaxableAmt = pieceCost * qty;
    const originalPieceTaxableAmt = product.original_piece_taxable_amt ? parseFloat(product.original_piece_taxable_amt) : pieceTaxableAmt;
    const calculatedDiscount = (pieceTaxableAmt * fixedPercentageDiscount) / 100;
    const updatedPieceTaxableAmt = originalPieceTaxableAmt - calculatedDiscount - discountAmt;
    const taxAmt = (taxPercent * updatedPieceTaxableAmt) / 100;
    const totalPrice = updatedPieceTaxableAmt + taxAmt;

    return {
      ...product,
      original_piece_taxable_amt: originalPieceTaxableAmt.toFixed(2),
      festival_discount: calculatedDiscount.toFixed(2),
      festival_discount_percentage: percentageDiscount.toFixed(2),
      festival_discount_on_rate: rateDiscount.toFixed(2),
      piece_taxable_amt: updatedPieceTaxableAmt.toFixed(2),
      tax_amt: taxAmt.toFixed(2),
      total_price: totalPrice.toFixed(2),
    };
  }

  const calculatedDiscount = (makingCharges * percentageDiscount) / 100 + (rateDiscount / 10) * grossWeight;
  const totalBeforeTax = rateAmt + stonePrice + makingCharges + hmCharges - calculatedDiscount - discountAmt;
  const taxAmt = (totalBeforeTax * taxPercent) / 100;
  const updatedTotalPrice = totalBeforeTax + taxAmt;
  
  return {
    ...product,
    original_total_price: product.original_total_price ? product.original_total_price : parseFloat(product.total_price || 0).toFixed(2),
    festival_discount: calculatedDiscount.toFixed(2),
    festival_discount_percentage: percentageDiscount.toFixed(2),
    festival_discount_on_rate: rateDiscount.toFixed(2),
    tax_amt: taxAmt.toFixed(2),
    total_price: updatedTotalPrice.toFixed(2),
  };
}

// Compute totals for the estimate given amounts and discount/tax
export function computeEstimateTotals({
  allProducts = [],
  oldTableData = [],
  oldSalesData = [],
  schemeTableData = [],
  saleReturnData = [],
  discount = 0,
  taxRate = 3,
}) {
  // Helper to safely parse numbers that might include currency symbols or commas
  const parseSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    const sanitized = String(value).replace(/[^0-9.-]/g, '');
    const num = parseFloat(sanitized);
    return isNaN(num) ? 0 : num;
  };
  // Calculate original taxable base (before discount) and current taxable base
  let originalTaxableBase = 0;
  let currentTaxableBase = 0;
  
  for (const product of allProducts) {
    // Use original_piece_taxable_amt if available (before discount), otherwise use piece_taxable_amt
    const originalPieceTaxable = parseFloat(product.original_piece_taxable_amt || product.piece_taxable_amt);
    const currentPieceTaxable = parseFloat(product.piece_taxable_amt);
    
    if (!isNaN(originalPieceTaxable) && originalPieceTaxable > 0) {
      originalTaxableBase += originalPieceTaxable;
    } else {
      // Fallback: derive from total_price by removing taxRate
      const totalPrice = parseFloat(product.total_price) || 0;
      if (totalPrice > 0) {
        const derivedTaxable = totalPrice / (1 + (taxRate / 100));
        originalTaxableBase += derivedTaxable;
      }
    }
    
    if (!isNaN(currentPieceTaxable) && currentPieceTaxable > 0) {
      currentTaxableBase += currentPieceTaxable;
    } else {
      // Fallback: derive from total_price by removing taxRate
      const totalPrice = parseFloat(product.total_price) || 0;
      if (totalPrice > 0) {
        const derivedTaxable = totalPrice / (1 + (taxRate / 100));
        currentTaxableBase += derivedTaxable;
      }
    }
  }
  
  // Use original taxable base for discount calculation to avoid double-counting
  const taxableBase = originalTaxableBase > 0 ? originalTaxableBase : currentTaxableBase;

  const totalMakingCharges = allProducts.reduce((sum, product) => sum + parseSafeNumber(product.making_charges), 0);
  // Prefer table data; if empty, fall back to form data to reflect live URD entries in create mode
  const urdSource = Array.isArray(oldTableData) && oldTableData.length > 0 ? oldTableData : (Array.isArray(oldSalesData) ? oldSalesData : []);
  const oldItemsAmount = urdSource.reduce((sum, item) => sum + parseSafeNumber(item.total_amount), 0);
  const schemeAmount = schemeTableData.reduce((sum, item) => sum + parseSafeNumber(item.total_amount), 0);
  // Sales Return should consider NET amount (taxable + tax). Prefer total_price when available,
  // but fall back to taxable + tax if API provides separate fields.
  const salesAmountToPass = saleReturnData.reduce((sum, item) => {
    // Sales SR logic: net = (taxable + tax) and exclude HM charges if present
    const net = (() => {
      const totalPrice = parseSafeNumber(item.total_price);
      const taxAmt = parseSafeNumber(item.tax_amt);
      const taxable = parseSafeNumber(
        item.salesTaxableAmount !== undefined ? item.salesTaxableAmount :
        (item.sales_taxable_amt !== undefined ? item.sales_taxable_amt :
        (item.piece_taxable_amt !== undefined ? item.piece_taxable_amt : item.taxable_amount))
      );
      const hm = parseSafeNumber(item.hm_charges);
      if (totalPrice > 0) return totalPrice - hm;
      if (taxable > 0 || taxAmt > 0) return (taxable + taxAmt) - hm;
      const taxPercent = parseSafeNumber(item.tax_percent);
      if (taxPercent > 0 && taxable > 0) return (taxable * (1 + taxPercent / 100)) - hm;
      return 0;
    })();
    return sum + net;
  }, 0);

  // Calculate discount amount from actual product discounts
  const totalProductDiscount = allProducts.reduce((sum, product) => {
    return sum + parseSafeNumber(product.disscount || 0);
  }, 0);
  
  // Calculate total festival discount from all products
  const totalFestivalDiscount = allProducts.reduce((sum, product) => {
    const festivalDiscount = parseSafeNumber(product.festival_discount || 0);
    return sum + festivalDiscount;
  }, 0);
  
  // Use the actual product discount amount, but fall back to percentage calculation if no product discounts
  const discountAmt = totalProductDiscount > 0 ? totalProductDiscount : (taxableBase * parseFloat(discount || 0)) / 100;
  
  // Festival discount should be deducted from total amount before calculating taxable amount
  const taxableAmount = taxableBase - discountAmt - totalFestivalDiscount;
  const taxAmount = (taxableAmount * taxRate) / 100;
  const netAmount = taxableAmount + taxAmount;
  const netPayableAmount = netAmount - oldItemsAmount - salesAmountToPass;
  

  const effectiveDiscountPercentage = parseFloat(discount || 0);

  return {
    totalAmount: parseFloat(taxableBase.toFixed(2)),
    totalMakingCharges: parseFloat(totalMakingCharges.toFixed(2)),
    taxableAmount: parseFloat(taxableAmount.toFixed(2)),
    discountAmt: parseFloat(discountAmt.toFixed(2)),
    festivalDiscountAmt: parseFloat(totalFestivalDiscount.toFixed(2)),
    taxAmount: parseFloat(taxAmount.toFixed(2)),
    netAmount: parseFloat(netAmount.toFixed(2)),
    oldItemsAmount: parseFloat(oldItemsAmount.toFixed(2)),
    schemeAmount: parseFloat(schemeAmount.toFixed(2)),
    salesAmountToPass: parseFloat(salesAmountToPass.toFixed(2)),
    netPayableAmount: Math.round(netPayableAmount),
    effectiveDiscountPercentage: parseFloat(effectiveDiscountPercentage.toFixed(2)),
  };
}


